import React, { createContext, useContext, useState } from "react";
import { APP_DICT } from "./appDict";

export const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: null, a: null });
export const useLang = () => useContext(LanguageContext);

const en = {
  nav: {
    links: {
      home: "Home",
      about: "About",
      services: "Services",
      packages: "Packages",
      gallery: "Gallery",
      contact: "Contact",
    },
  },
  hero: {
    eyebrow: "Domestic hygiene & handyman care",
    lines: [
      ["HYGIENE", "white"],
      ["ELEVATED.", "gold"],
      ["FIXED", "outline"],
      ["RIGHT.", "outline"],
    ],
    sub: "Deep cleaning, water-tank sanitization, pest control and expert AC, refrigerator & appliance repair — one trusted team at your doorstep, on your schedule.",
    book: "Book on WhatsApp",
    chips: ["Vetted technicians", "Non-toxic products", "Same-day response"],
    openNow: "Open now",
    six: "6 services · 1 team",
  },
  marquee: [
    "House Deep Cleaning",
    "Water-Tank Sanitization",
    "Pest Control",
    "AC Service & Repair",
    "Refrigerator Care",
    "Appliance Repair",
    "Handyman On Call",
  ],
  about: {
    tag: "About OGO",
    title: [
      ["One team for everything your ", ""],
      ["home needs.", "gold"],
    ],
    sub: "OGO Hygiene Solutions brings professional deep cleaning, water-tank sanitization, pest control and skilled appliance repair under a single, accountable roof — no juggling vendors, no guesswork on quality.",
    promises: [
      { t: "Vetted & trained crew", d: "Background-verified technicians, uniformed and equipped." },
      { t: "Non-toxic products", d: "Hospital-grade, child- and pet-safe cleaning agents only." },
      { t: "On-time, every time", d: "Booked slots honoured — same-day for urgent jobs." },
      { t: "Spotless guarantee", d: "Not happy with a patch? We re-do it, free." },
    ],
    badgeTag: "The OGO standard",
    badgeText: "Every home treated like a sanctuary — measured, inspected, signed off.",
  },
  services: {
    tag: "What we do",
    title: [
      ["Six services. ", ""],
      ["Zero hassle.", "outline"],
    ],
    sub: "Tap any card to book instantly on WhatsApp — a coordinator confirms your slot within minutes.",
    book: "Book",
    items: [
      {
        id: "house-cleaning",
        idx: "01 / House Deep Cleaning",
        title: "Full-Home Deep Cleaning",
        desc: "Bedrooms to balconies — degreasing, descaling, glass, floors and fixtures, restored to showroom condition with our multi-stage checklist.",
        tags: [["Most booked", "gold"]],
      },
      {
        id: "water-tank",
        idx: "02 / Water-Tank Sanitization",
        title: "Water-Tank Cleaning",
        desc: "Drain, scrub, disinfect and UV-safe rinse — crystal-clear storage water for your family.",
        tags: [["Purity certified", "mint"]],
      },
      {
        id: "ac-repair",
        idx: "03 / AC Service & Repair",
        title: "AC Repair & Service",
        desc: "Jet-pump deep service, gas top-up, cooling faults and installs — split & window units.",
        tags: [["Cooling in 1 visit", "gold"]],
      },
      {
        id: "pest-control",
        idx: "04 / Pest Control",
        title: "Pest Control",
        desc: "Cockroaches, ants, termites, mosquitoes — odourless, non-toxic sprays & gels safe for kids and pets.",
        tags: [["Eco-safe", "mint"]],
      },
      {
        id: "refrigerator-repair",
        idx: "05 / Refrigerator & Appliance Repair",
        title: "Refrigerator & Appliance Repair",
        desc: "Fridge cooling issues, washing machines, microwaves, chimneys — genuine spares, 90-day service warranty.",
        tags: [["90-day warranty", "gold"]],
      },
    ],
    handyman: {
      idx: "06 / Domestic Handyman",
      title: "Small fixes, big relief — on call",
      desc: "Electrical points, plumbing leaks, latch & lock fittings, curtains, shelves, minor carpentry — one visit, done right.",
      cta: "Call a handyman",
    },
  },
  packages: {
    tag: "Packages",
    title: [
      ["Fair packages. ", ""],
      ["Honest quotes.", "gold"],
    ],
    sub: "Final price depends on your home's size and condition — message us for a free inspection quote. No hidden charges, ever.",
    quote: "Get quote on WhatsApp",
    items: [
      {
        id: "essential",
        tag: "Routine refresh",
        name: "Essential Clean",
        items: ["Bedrooms, kitchen & bathrooms", "Dusting, sweeping & mopping", "Trash-out & tidy-up", "1 crew · ~2–3 hours"],
      },
      {
        id: "deep-restore",
        tag: "Top to bottom",
        name: "Deep Restore",
        items: ["Everything in Essential", "Kitchen degreasing & descaling", "Bathroom tile & fitting descaling", "Windows, grills & balconies"],
        featured: true,
      },
      {
        id: "master-appliance",
        tag: "Keep them running",
        name: "Appliance Master",
        items: ["AC deep service (up to 2 units)", "Refrigerator deep clean", "Washing machine & microwave check", "Genuine spares at MRP"],
      },
      {
        id: "villa-deluxe",
        tag: "The full works",
        name: "Villa Deluxe",
        items: ["Full-property deep clean", "Water-tank sanitization", "Complete pest-control round", "Appliance care included"],
      },
    ],
  },
  gallery: {
    tag: "Gallery",
    title: [
      ["Proof, not ", ""],
      ["promises.", "outline"],
    ],
    sub: "Real visits, real transformations — from first spray to final shine.",
    realTag: "OGO work photo",
    tags: [
      "Floor & Tile Deep Clean",
      "Surface Detailing",
      "AC Service Visit",
      "Full-Home Refresh",
      "Glass & Windows",
      "Water-Tank Sanitization",
      "Pest Control Round",
      "Sanitization Pass",
    ],
  },
  reviews: {
    tag: "Reviews",
    title: [
      ["Homes that ", ""],
      ["glow", "gold"],
      [" after OGO.", ""],
    ],
    sub: "Feedback from families who booked through WhatsApp and stayed for the shine.",
    items: [
      {
        name: "Sample review — Lakshmi K.",
        role: "Full-home deep clean",
        text: "The crew arrived on time, covered every corner and left the kitchen looking brand new. Booking on WhatsApp took less than a minute.",
      },
      {
        name: "Sample review — Arun M.",
        role: "AC service & repair",
        text: "Our AC was barely cooling. One visit, deep service and gas top-up later, it's like new again. Polite, tidy technicians.",
      },
      {
        name: "Sample review — Fathima S.",
        role: "Pest control + tank cleaning",
        text: "Odourless pest treatment and our overhead tank was spotless inside. Finally one company that handles everything properly.",
      },
    ],
  },
  contact: {
    tag: "Contact",
    title: [
      ["Talk to us. ", ""],
      ["We answer fast.", "gold"],
    ],
    sub: "No forms, no waiting queues — reach a real coordinator directly and get your slot confirmed in minutes.",
    labels: { phone: "Call us", whatsapp: "WhatsApp", email: "Email" },
    chat: "Chat instantly",
  },
  footer: {
    explore: "Explore",
    reach: "Reach us",
    desc: "Deep cleaning, sanitization, pest control and appliance repair — delivered to your doorstep by a crew that treats every home like a sanctuary.",
    whatsappUs: "WhatsApp us anytime",
    backToTop: "Back to top",
  },
};

const te = {
  nav: {
    links: {
      home: "హోమ్",
      about: "మా గురించి",
      services: "సేవలు",
      packages: "ప్యాకేజీలు",
      gallery: "గ్యాలరీ",
      contact: "సంప్రదించండి",
    },
  },
  hero: {
    eyebrow: "ఇంటి పరిశుభ్రత & హ్యాండీమ్యాన్ సేవలు",
    lines: [
      ["మీ ఇల్లు", "white"],
      ["తళుకు తళుకు.", "gold"],
      ["రిపేర్లు", "outline"],
      ["సరిగా ఫిక్స్.", "outline"],
    ],
    sub: "డీప్ క్లీనింగ్, వాటర్ ట్యాంక్ శుద్ధి, పెస్ట్ కంట్రోల్ మరియు AC, ఫ్రిజ్ & అప్లయన్స్ రిపేర్లు — ఒకే నమ్మకమైన టీమ్, మీ ఇంటి వద్దకే, మీ టైమ్‌కి.",
    book: "WhatsApp లో బుక్ చేయండి",
    chips: ["వెరిఫైడ్ టెక్నీషియన్లు", "విషరహిత ఉత్పత్తులు", "అదే రోజు సర్వీస్"],
    openNow: "ఇప్పుడు ఓపెన్",
    six: "6 సేవలు · 1 టీమ్",
  },
  marquee: [
    "హౌస్ డీప్ క్లీనింగ్",
    "వాటర్ ట్యాంక్ శుద్ధి",
    "పెస్ట్ కంట్రోల్",
    "AC సర్వీస్ & రిపేర్",
    "ఫ్రిజ్ కేర్",
    "అప్లయన్స్ రిపేర్",
    "హ్యాండీమ్యాన్ ఆన్ కాల్",
  ],
  about: {
    tag: "OGO గురించి",
    title: [
      ["మీ ఇంటికి కావాల్సినవన్నీ — ", ""],
      ["ఒకే టీమ్.", "gold"],
    ],
    sub: "OGO Hygiene Solutions — ప్రొఫెషనల్ డీప్ క్లీనింగ్, ట్యాంక్ శుద్ధి, పెస్ట్ కంట్రోల్ మరియు స్కిల్డ్ అప్లయన్స్ రిపేర్ ఒకే చోట, ఒకే బాధ్యతతో. వేర్వేరు వెండర్ల కోసం వెతకవలసిల్లదు.",
    promises: [
      { t: "వెరిఫైడ్ & ట్రైన్డ్ క్రూ", d: "బ్యాక్‌గ్రౌండ్ వెరిఫైడ్ టెక్నీషియన్లు, యూనిఫాం & పూర్తి ఎక్విప్‌మెంట్‌తో." },
      { t: "విషరహిత ఉత్పత్తులు", d: "పిల్లలు & పెంపుడు జంతువులకు సురక్షితమైన ఆసుపత్రి-స్థాయి క్లీనింగ్ ఏజెంట్లు మాత్రమే." },
      { t: "టైమ్‌కి, ప్రతిసారీ", d: "బుక్ చేసిన స్లాట్లు ఖచ్చితంగా — అత్యవసర పనులకు అదే రోజు సర్వీస్." },
      { t: "మచ్చలేని గ్యారంటీ", d: "ఒక మచ్చ కూడా నచ్చకపోతే? ఉచితంగా మళ్లీ చేస్తాం." },
    ],
    badgeTag: "OGO స్టాండర్డ్",
    badgeText: "ప్రతి ఇంటినీ అత్యున్నత శ్రద్ధతో — కొలిచి, తనిఖీ చేసి, సైన్-ఆఫ్.",
  },
  services: {
    tag: "మేము ఏం చేస్తాం",
    title: [
      ["ఆరు సేవలు. ", ""],
      ["జీరో హసిల్.", "outline"],
    ],
    sub: "ఏ కార్డు మీదైనా ట్యాప్ చేయండి — WhatsApp లో వెంటనే బుక్ అవుతుంది. కొన్ని నిమిషాల్లో స్లాట్ కన్ఫర్మేషన్.",
    book: "బుక్",
    items: [
      {
        id: "house-cleaning",
        idx: "01 / హౌస్ డీప్ క్లీనింగ్",
        title: "ఫుల్-హోమ్ డీప్ క్లీనింగ్",
        desc: "బెడ్‌రూమ్ల నుండి బాల్కనీల వరకు — డీగ్రీజింగ్, డీస్కేలింగ్, గ్లాస్, ఫ్లోర్లు & ఫిక్చర్లు — షోరూమ్ కండిషన్‌కి రెడీ.",
        tags: [["ఎక్కువగా బుక్", "gold"]],
      },
      {
        id: "water-tank",
        idx: "02 / వాటర్ ట్యాంక్ శుద్ధి",
        title: "వాటర్ ట్యాంక్ క్లీనింగ్",
        desc: "డ్రెయిన్, స్క్రబ్, క్రిమిసంహారక & సేఫ్ రిన్స్ — మీ కుటుంబానికి స్వచ్ఛమైన నిల్వ నీరు.",
        tags: [["స్వచ్ఛత సర్టిఫైడ్", "mint"]],
      },
      {
        id: "ac-repair",
        idx: "03 / AC సర్వీస్ & రిపేర్",
        title: "AC రిపేర్ & సర్వీస్",
        desc: "జెట్-పంప్ డీప్ సర్వీస్, గ్యాస్ టాప్-అప్, కూలింగ్ ఫాల్ట్లు & ఇన్‌స్టాలేషన్ — స్ప్లిట్ & విండో ఏసీలు.",
        tags: [["1 విజిట్‌లో కూలింగ్", "gold"]],
      },
      {
        id: "pest-control",
        idx: "04 / పెస్ట్ కంట్రోల్",
        title: "పెస్ట్ కంట్రోల్",
        desc: "కాక్రోచ్‌లు, చీములు, చెదల్పురుగులు, దోమలు — వాసన లేని, విషరహిత స్ప్రే & జెల్లు — పిల్లలకు, పెంపుడు జంతువులకు సురక్షితం.",
        tags: [["ఎకో-సేఫ్", "mint"]],
      },
      {
        id: "refrigerator-repair",
        idx: "05 / ఫ్రిజ్ & అప్లయన్స్ రిపేర్",
        title: "ఫ్రిజ్ & అప్లయన్స్ రిపేర్",
        desc: "ఫ్రిజ్ కూలింగ్ సమస్యలు, వాషింగ్ మెషీన్లు, మైక్రోవేవ్‌లు, చిమ్నీలు — ఒరిజినల్ స్పేర్స్, 90-రోజుల సర్వీస్ వారంటీ.",
        tags: [["90-రోజుల వారంటీ", "gold"]],
      },
    ],
    handyman: {
      idx: "06 / డోమెస్టిక్ హ్యాండీమ్యాన్",
      title: "చిన్న ఫిక్స్‌లు, పెద్ద ఉపశమనం — కాల్ చేయండి",
      desc: "ఎలక్ట్రికల్ పాయింట్లు, ప్లంబింగ్ లీక్‌లు, లాక్ & లాచ్ ఫిటింగ్లు, కర్టెన్లు, షెల్ఫ్‌లు, చిన్న కార్పెంట్రీ — ఒకే విజిట్‌లో పరిష్కారం.",
      cta: "హ్యాండీమ్యాన్ కాల్ చేయండి",
    },
  },
  packages: {
    tag: "ప్యాకేజీలు",
    title: [
      ["న్యాయమైన ప్యాకేజీలు. ", ""],
      ["నిజాయితీ కోట్స్.", "gold"],
    ],
    sub: "తుది ధర మీ ఇల్లు సైజు & పరిస్థితిపై ఆధారపడి ఉంటుంది — ఉచిత ఇన్‌స్పెక్షన్ కోట్ కోసం మాకు మెసేజ్ చేయండి. దాగుడు ఛార్జీలు అస్సలు లేవు.",
    quote: "WhatsApp లో కోట్ తీసుకోండి",
    items: [
      {
        id: "essential",
        tag: "రోజువారీ రిఫ్రెష్",
        name: "ఎసెన్షియల్ క్లీన్",
        items: ["బెడ్‌రూమ్లు, కిచెన్ & బాత్‌రూమ్లు", "డస్టింగ్, అలికిడి & మాపింగ్", "చెత్త బయటకు & షగటింపు", "1 క్రూ · ~2–3 గంటలు"],
      },
      {
        id: "deep-restore",
        tag: "పైనుండి కింది వరకు",
        name: "డీప్ రెస్టోర్",
        items: ["ఎసెన్షియల్‌లో అన్నీ", "కిచెన్ డీగ్రీజింగ్ & డీస్కేలింగ్", "బాత్‌రూమ్ టైల్స్ & ఫిటింగ్ల డీస్కేలింగ్", "విండోలు, గ్రిల్స్ & బాల్కనీలు"],
        featured: true,
      },
      {
        id: "master-appliance",
        tag: "పరికరాల సంరక్షణ",
        name: "అప్లయన్స్ మాస్టర్",
        items: ["AC డీప్ సర్వీస్ (2 యూనిట్ల వరకు)", "ఫ్రిజ్ డీప్ క్లీన్", "వాషింగ్ మెషీన్ & మైక్రోవేవ్ చెక్", "ఒరిజినల్ స్పేర్స్ ఎం.ఆర్.పి ధరలకే"],
      },
      {
        id: "villa-deluxe",
        tag: "పూర్తి ప్యాకేజీ",
        name: "విల్లా డీలక్స్",
        items: ["ఫుల్-ప్రాపర్టీ డీప్ క్లీన్", "వాటర్ ట్యాంక్ శుద్ధి", "కంప్లీట్ పెస్ట్ కంట్రోల్ రౌండ్", "అప్లయన్స్ కేర్ ఇన్‌క్లూడెడ్"],
      },
    ],
  },
  gallery: {
    tag: "గ్యాలరీ",
    title: [
      ["రుజువులే, ", ""],
      ["వాగుదలలు కాదు.", "outline"],
    ],
    sub: "నిజమైన విజిట్లు, నిజమైన మార్పులు — మొదటి స్ప్రే నుండి చివరి మెరుపు వరకు.",
    realTag: "OGO వర్క్ ఫోటో",
    tags: [
      "ఫ్లోర్ & టైల్స్ డీప్ క్లీన్",
      "సర్ఫేస్ డీటెయిలింగ్",
      "AC సర్వీస్ విజిట్",
      "ఫుల్-హోమ్ రిఫ్రెష్",
      "గ్లాస్ & విండోలు",
      "వాటర్ ట్యాంక్ శుద్ధి",
      "పెస్ట్ కంట్రోల్ రౌండ్",
      "శానిటైజేషన్ పాస్",
    ],
  },
  reviews: {
    tag: "రివ్యూలు",
    title: [
      ["OGO తర్వాత ఇళ్లు ", ""],
      ["మెరుస్తాయి.", "gold"],
    ],
    sub: "WhatsApp ద్వారా బుక్ చేసిన కుటుంబాల ఫీడ్‌బ్యాక్.",
    items: [
      {
        name: "సాంపిల్ రివ్యూ — లక్ష్మీ K.",
        role: "ఫుల్-హోమ్ డీప్ క్లీన్",
        text: "క్రూ టైమ్‌కి వచ్చి, ప్రతి మూలను శుభ్రం చేసి, కిచెన్‌ను కొత్తదిలా చేసి వదిలారు. WhatsApp లో బుకింగ్ ఒక నిమిషం పట్టింది.",
      },
      {
        name: "సాంపిల్ రివ్యూ — అరుణ్ M.",
        role: "AC సర్వీస్ & రిపేర్",
        text: "మా AC పెద్దగా కూల్ చేయట్లేదు. ఒక విజిట్‌లో డీప్ సర్వీస్ & గ్యాస్ టాప్-అప్ — ఇప్పుడు కొత్తదిలా పని చేస్తోంది. మర్యాలుగా, చక్కగా పని చేశారు.",
      },
      {
        name: "సాంపిల్ రివ్యూ — ఫాతిమా S.",
        role: "పెస్ట్ కంట్రోల్ + ట్యాంక్ క్లీనింగ్",
        text: "వాసన లేని పెస్ట్ ట్రీట్‌మెంట్ & మా ట్యాంక్ లోపల కూడా తళుకు తళుకు. అన్నీ ఒకే కంపెనీ ఇలా సరిగా చేసిన చాలా రోజులు అయ్యింది.",
      },
    ],
  },
  contact: {
    tag: "సంప్రదించండి",
    title: [
      ["మాతో మాట్లాడండి. ", ""],
      ["వెంటనే రెస్పాన్స్.", "gold"],
    ],
    sub: "ఫారమ్లు లేవు, వేచి ఉండాల్సిన పనిలేదు — నేరుగా మా కోఆర్డినేటర్‌ను కాంటాక్ట్ చేయండి, కొన్ని నిమిషాల్లో స్లాట్ కన్ఫర్మ్.",
    labels: { phone: "కాల్ చేయండి", whatsapp: "WhatsApp", email: "ఇమెయిల్" },
    chat: "వెంటనే చాట్ చేయండి",
  },
  footer: {
    explore: "చూడండి",
    reach: "మమ్మల్ని సంప్రదించండి",
    desc: "డీప్ క్లీనింగ్, శుద్ధి, పెస్ట్ కంట్రోల్ & అప్లయన్స్ రిపేర్ — ప్రతి ఇంటిని అత్యున్నతంగా చూసే మా క్రూ, మీ ఇంటి ముంగిటికే.",
    whatsappUs: "ఎప్పుడైనా WhatsApp చేయండి",
    backToTop: "పైకి",
  },
};

export const DICT = { en, te };

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("te");
  const value = { lang, setLang, t: DICT[lang], a: APP_DICT[lang] };
  return React.createElement(LanguageContext.Provider, { value }, children);
}
